import { type TypedDocumentNode, gql } from '@apollo/client';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';

import { PlaceholderSlot } from '@sorare/marketplace/src/components/commonCard/RevealPackCardsDialog/CollectionAnimation/CollectionAnimationClub/PlaceholderSlot';

import { CardsCountBadge } from 'components/collections/CardsCountBadge';

import type { CollectionSlot_userCardCollectionSlot } from './__generated__/index.graphql';

type Props = {
  userCardCollectionSlot: Nullable<CollectionSlot_userCardCollectionSlot>;
  small?: boolean;
  rarity: Nullable<Rarity>;
};

export const CollectionSlot = ({
  userCardCollectionSlot,
  small = false,
  rarity,
}: Props) => {
  const { highlightedCardCollectionCard, cardCollectionCardsCount } =
    userCardCollectionSlot || {};
  const { anyCard } = highlightedCardCollectionCard || {};
  const isLaptopAndAbove = useIsLaptop();
  const openDialog = useOpenDialog();
  if (cardCollectionCardsCount === 0 || !anyCard) {
    return <PlaceholderSlot slot={userCardCollectionSlot} small={small} />;
  }
  return (
    <CardsCountBadge
      size="small"
      userCardCollectionSlot={userCardCollectionSlot}
      rarity={rarity}
    >
      <ButtonBase onClick={() => openDialog(DialogKey.card, anyCard.slug)}>
        <CardImg
          src={anyCard.pictureUrl}
          alt=""
          width={isLaptopAndAbove ? 160 : 80}
        />
      </ButtonBase>
    </CardsCountBadge>
  );
};

CollectionSlot.fragments = {
  userCardCollectionSlot: gql`
    fragment CollectionSlot_userCardCollectionSlot on UserCardCollectionSlot {
      slug
      cardCollectionCardsCount
      highlightedCardCollectionCard {
        id
        anyCard {
          slug
          pictureUrl
        }
      }
      ...PlaceholderSlot_userCardCollectionSlot
      ...CardsCountBadge_userCardCollectionSlot
    }
    ${PlaceholderSlot.fragments.userCardCollectionSlot}
    ${CardsCountBadge.fragments.userCardCollectionSlot}
  ` as TypedDocumentNode<CollectionSlot_userCardCollectionSlot>,
};
